import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { bookingsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Calendar, User, MapPin, Loader2, Check, X, MessageSquare } from 'lucide-react';
import { formatDate, formatCurrency, getStatusColor } from '../../lib/utils';
import { toast } from 'sonner';

const defaultImage = 'https://images.unsplash.com/photo-1563116128-261f46267cac?w=400';

export default function HostRequests() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightBooking = searchParams.get('booking');
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchBookings();
  }, []);

  useEffect(() => {
    if (highlightBooking && bookings.length > 0) {
      const booking = bookings.find((b) => b.id === highlightBooking);
      if (booking) {
        setSelectedBooking(booking);
        setActiveTab('pending');
      }
    }
  }, [highlightBooking, bookings]);

  const fetchBookings = async () => {
    try {
      const response = await bookingsApi.getAll();
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    setProcessing(true);
    try {
      await bookingsApi.updateStatus(bookingId, status);
      toast.success(`Booking ${status}`);
      fetchBookings();
      setSelectedBooking(null);
    } catch (error) {
      toast.error('Failed to update booking');
    } finally {
      setProcessing(false);
    }
  };

  const filterBookings = (status) => {
    if (status === 'active') {
      return bookings.filter((b) => b.status === 'accepted');
    }
    return bookings.filter((b) => b.status === status);
  };

  const BookingRow = ({ booking }) => {
    const listing = booking.listing || {};
    const guest = booking.guest || {};

    return (
      <div
        className={`bg-farmhouse-paper border rounded-md p-4 cursor-pointer card-hover ${
          highlightBooking === booking.id ? 'border-farmhouse-saddle' : 'border-farmhouse-subtle'
        }`}
        onClick={() => setSelectedBooking(booking)}
        data-testid={`booking-row-${booking.id}`}
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <img
            src={listing.photos?.[0] || defaultImage}
            alt={listing.title}
            className="w-full md:w-24 h-24 object-cover rounded-md"
            onError={(e) => { e.target.src = defaultImage; }}
          />
          
          <div className="flex-1">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-medium text-farmhouse-cream">{listing.title}</h3>
                <div className="flex items-center gap-2 text-sm text-farmhouse-muted mt-1">
                  <User className="w-4 h-4" />
                  <span>{guest.name}</span>
                </div>
              </div>
              <Badge className={`${getStatusColor(booking.status)} border-0 capitalize`}>
                {booking.status}
              </Badge>
            </div>
            
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-farmhouse-muted">
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{formatDate(booking.check_in)} - {formatDate(booking.check_out)}</span>
              </div>
              <span>{booking.num_animals} animal{booking.num_animals > 1 ? 's' : ''}</span>
              <span className="font-medium text-farmhouse-cream">{formatCurrency(booking.total_amount)}</span>
            </div>
          </div>

          {booking.status === 'pending' && (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateStatus(booking.id, 'accepted');
                }}
                className="bg-farmhouse-moss hover:bg-farmhouse-moss/80 text-white"
                data-testid="accept-booking"
              >
                <Check className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUpdateStatus(booking.id, 'declined');
                }}
                className="border-farmhouse-clay text-farmhouse-clay hover:bg-farmhouse-clay/10"
                data-testid="decline-booking"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="host-requests-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-8">
          Booking Requests
        </h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-farmhouse-paper border border-farmhouse-subtle mb-6">
            <TabsTrigger 
              value="pending"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Pending ({filterBookings('pending').length})
            </TabsTrigger>
            <TabsTrigger 
              value="active"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Active ({filterBookings('active').length})
            </TabsTrigger>
            <TabsTrigger 
              value="completed"
              className="data-[state=active]:bg-farmhouse-saddle data-[state=active]:text-white"
            >
              Completed ({filterBookings('completed').length})
            </TabsTrigger>
          </TabsList>

          {['pending', 'active', 'completed'].map((tab) => (
            <TabsContent key={tab} value={tab}>
              {filterBookings(tab).length > 0 ? (
                <div className="space-y-4">
                  {filterBookings(tab).map((booking) => (
                    <BookingRow key={booking.id} booking={booking} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-farmhouse-paper border border-farmhouse-subtle rounded-md">
                  <Calendar className="w-16 h-16 mx-auto mb-4 text-farmhouse-subtle" />
                  <h3 className="font-serif text-xl font-semibold text-farmhouse-cream mb-2">
                    No {tab} bookings
                  </h3>
                  <p className="text-farmhouse-muted">
                    {tab === 'pending' ? 'New requests will appear here' : `No ${tab} bookings yet`}
                  </p>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        {/* Booking Detail Dialog */}
        <Dialog open={!!selectedBooking} onOpenChange={(open) => !open && setSelectedBooking(null)}>
          <DialogContent className="bg-farmhouse-paper border-farmhouse-subtle max-w-2xl">
            {selectedBooking && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-serif text-2xl text-farmhouse-cream">
                    Booking Details
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 mt-4">
                  {/* Guest Info */}
                  <div className="flex items-center gap-4 p-4 bg-farmhouse-charcoal rounded-md">
                    <div className="avatar w-14 h-14 text-lg bg-farmhouse-saddle text-white">
                      {selectedBooking.guest?.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium text-farmhouse-cream text-lg">
                        {selectedBooking.guest?.name}
                      </p>
                      <p className="text-farmhouse-muted">{selectedBooking.guest?.email}</p>
                    </div>
                  </div>

                  {/* Booking Info */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-farmhouse-muted text-sm">Check-in</p>
                      <p className="text-farmhouse-cream font-medium">{formatDate(selectedBooking.check_in)}</p>
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Check-out</p>
                      <p className="text-farmhouse-cream font-medium">{formatDate(selectedBooking.check_out)}</p>
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Animals</p>
                      <p className="text-farmhouse-cream font-medium">{selectedBooking.num_animals}</p>
                    </div>
                    <div>
                      <p className="text-farmhouse-muted text-sm">Total</p>
                      <p className="text-farmhouse-cream font-medium text-lg">{formatCurrency(selectedBooking.total_amount)}</p>
                    </div>
                  </div>

                  {/* Message */}
                  {selectedBooking.message && (
                    <div>
                      <p className="text-farmhouse-muted text-sm mb-2">Message from Guest</p>
                      <div className="p-4 bg-farmhouse-charcoal rounded-md">
                        <p className="text-farmhouse-cream">{selectedBooking.message}</p>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  {selectedBooking.status === 'pending' && (
                    <div className="flex gap-3 pt-4 border-t border-farmhouse-subtle">
                      <Button
                        onClick={() => handleUpdateStatus(selectedBooking.id, 'accepted')}
                        className="flex-1 bg-farmhouse-moss hover:bg-farmhouse-moss/80 text-white"
                        disabled={processing}
                      >
                        {processing ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                          <>
                            <Check className="w-4 h-4 mr-2" />
                            Accept
                          </>
                        )}
                      </Button>
                      <Button
                        onClick={() => handleUpdateStatus(selectedBooking.id, 'declined')}
                        variant="outline"
                        className="flex-1 border-farmhouse-clay text-farmhouse-clay hover:bg-farmhouse-clay/10"
                        disabled={processing}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Decline
                      </Button>
                    </div>
                  )}

                  {selectedBooking.status === 'accepted' && (
                    <div className="flex gap-3 pt-4 border-t border-farmhouse-subtle">
                      <Button
                        onClick={() => navigate('/messages')}
                        className="flex-1 btn-primary"
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Message Guest
                      </Button>
                      <Button
                        onClick={() => handleUpdateStatus(selectedBooking.id, 'completed')}
                        variant="outline"
                        className="flex-1 border-farmhouse-subtle text-farmhouse-cream hover:bg-farmhouse-subtle"
                      >
                        Mark Complete
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
